// backup.repo — dump seluruh tabel bisnis → JSON, upload ke Storage 'backup', list, retensi.
import { supabase } from '@core/supabase';
import { listFiles, deleteFile, signedUrl, type StoredObject } from '@core/storage';

// Daftar tabel diambil LANGSUNG DARI DB (RPC public_tables, 0068) — daftar hardcoded selalu
// ketinggalan tiap ada migrasi baru (bug nyata: commission_payouts & daily_close tak ikut ter-backup).
// auth.users & file storage TIDAK termasuk (di luar jangkauan PostgREST).
export async function tableList(): Promise<string[]> {
  const { data, error } = await supabase().rpc('public_tables');
  if (error) throw new Error(`Daftar tabel tak terbaca: ${error.message}`);
  const rows = (data ?? []) as { tbl: string }[];
  if (!rows.length) throw new Error('Daftar tabel kosong — backup dibatalkan (cegah backup palsu-kosong).');
  return rows.map((r) => r.tbl);
}

export interface DumpResult { data: Record<string, unknown[]>; skipped: string[]; rows: number }

/** Dump tiap tabel — PAGINASI penuh (PostgREST cap ~1000/req → tabel besar tak boleh terpotong diam-diam).
 *  Tabel gagal (RLS/tak ada) dilewati (tercatat di skipped). */
const PAGE = 1000;
export async function dumpAll(): Promise<DumpResult> {
  const data: Record<string, unknown[]> = {};
  const skipped: string[] = [];
  let rows = 0;
  for (const t of await tableList()) {
    const all: unknown[] = [];
    let failed = false;
    for (let from = 0; ; from += PAGE) {
      const { data: r, error } = await supabase().from(t).select('*').range(from, from + PAGE - 1);
      if (error) { failed = true; break; }
      const batch = r ?? [];
      all.push(...batch);
      if (batch.length < PAGE) break; // halaman terakhir → tabel tuntas
    }
    if (failed) { skipped.push(t); continue; }
    data[t] = all; rows += all.length;
  }
  return { data, skipped, rows };
}

/** Upload snapshot JSON (upsert → timpa backup tanggal yang sama = 1/hari). */
export async function uploadBackup(path: string, json: string): Promise<void> {
  const blob = new Blob([json], { type: 'application/json' });
  const { error } = await supabase().storage.from('backup').upload(path, blob, { contentType: 'application/json', upsert: true });
  if (error) throw new Error(error.message);
}

export async function listBackups(): Promise<StoredObject[]> {
  return (await listFiles('backup', '')).sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
}

/** Retensi: hapus backup > `days` hari. Cutoff dari WAKTU SERVER (updatedAt terbaru) — bukan jam klien
 *  (cegah clock-skew klien menghapus backup yang baru dibuat). Timestamp tak-terparse → dipertahankan. */
export async function cleanupOld(days: number): Promise<number> {
  const files = await listFiles('backup', '');
  if (!files.length) return 0;
  const serverNow = Math.max(...files.map((f) => (f.updatedAt ? new Date(f.updatedAt).getTime() : 0)));
  const cutoff = serverNow - days * 86400000;
  const old = files.filter((f) => { const t = f.updatedAt ? new Date(f.updatedAt).getTime() : NaN; return !isNaN(t) && t < cutoff; });
  for (const f of old) { try { await deleteFile('backup', f.path); } catch { /* best-effort */ } }
  return old.length;
}

export const backupUrl = (path: string): Promise<string> => signedUrl('backup', path, 120);

/** Restore PENUH via RPC security-definer (Owner saja — guard di dalam fungsi DB).
 *  Lewat RPC karena RLS/trigger §26 sengaja memblok hapus/timpa transaksi dari aplikasi. */
export async function restoreAll(payload: Record<string, unknown>): Promise<{ rows: number; tables: Record<string, number> }> {
  const { data, error } = await supabase().rpc('restore_backup', { p_payload: payload });
  if (error) throw new Error(error.message);
  return data as { rows: number; tables: Record<string, number> };
}

/** Ambil isi file backup di server (untuk restore langsung dari daftar, tanpa unduh manual). */
export async function fetchBackup(path: string): Promise<string> {
  const { data, error } = await supabase().storage.from('backup').download(path);
  if (error) throw new Error(error.message);
  return data.text();
}
